import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDocs,
  writeBatch,
} from "firebase/firestore";

const Navbar = () => {
  const { user, userData } = useAuth(); // userData: Firestore user doc
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);

  // Fetch notifications (real-time)
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "referral_users", user.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(10),
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setNotifications(data);
      setUnread(data.filter((n) => !n.read).length);
    });
    return () => unsub();
  }, [user]);

  // Click outside to close profile dropdown & notification dropdown
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target))
        setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return;
    const notifs = await getDocs(
      collection(db, "referral_users", user.uid, "notifications"),
    );
    const batch = writeBatch(db);
    notifs.docs.forEach((docu) => {
      if (!docu.data().read) {
        batch.update(
          doc(db, "referral_users", user.uid, "notifications", docu.id),
          {
            read: true,
          },
        );
      }
    });
    await batch.commit();
  };

  const getDisplayName = (userData, user) =>
    userData?.fullName?.trim() ||
    user?.displayName?.trim() ||
    userData?.name?.trim() ||
    userData?.phone?.trim() ||
    user?.email?.split("@")[0] ||
    "User";

  return (
    <nav
      className="
  fixed top-0 w-full z-50
    backdrop-blur
    bg-gradient-to-r from-fuchsia-100 via-blue-50 to-sky-100
    border-b border-blue-100 shadow-sm
"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
        {/* Logo & Title */}
        <div className="flex items-center gap-3">
          <img
            src="/user-portal-logo.png"
            alt="Ceylon Web Mall"
            className="h-9 w-9 drop-shadow"
          />
          <span className="font-extrabold text-2xl text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-700 via-blue-600 to-indigo-700 tracking-tight select-none">
            Sales Agent Dashboard
          </span>
          {user && (
            <span className="text-sm md:text-base font-semibold text-blue-700/80">
              — {getDisplayName(userData, user)}
            </span>
          )}
        </div>

        {/* Nav Links */}
        <div className="flex gap-4 items-center">
          <NavLink
            to="/dashboard"
            className="nav-link font-semibold text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
          >
            Dashboard
          </NavLink>

          <NavLink
            to="/courses-to-promote"
            className="nav-link font-semibold text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
          >
            Promote
          </NavLink>

          <NavLink
            to="/referral"
            className="nav-link font-semibold text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
          >
            Team
          </NavLink>

          <NavLink
            to="/commissions"
            className="nav-link font-semibold text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
          >
            Commissions
          </NavLink>

          {/* Notifications Bell */}
          {user && (
            <div className="relative" ref={notifRef}>
              <button
                className="relative mr-2"
                onClick={() => setNotifOpen((v) => !v)}
                aria-label="Notifications"
              >
                <span className="inline-block">
                  <svg
                    className="h-7 w-7 text-blue-700"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 17h5l-1.405-1.405C18.367 15.104 18 14.552 18 14V11a6 6 0 10-12 0v3c0 .552-.367 1.104-.595 1.595L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold shadow">
                      {unread}
                    </span>
                  )}
                </span>
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-3 w-80 max-w-xs bg-white/95 border border-blue-200 rounded-xl shadow-xl z-50 backdrop-blur animate-fadeIn overflow-hidden">
                  <div className="px-4 py-2 border-b font-semibold flex justify-between items-center">
                    Notifications
                    {unread > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs px-2 py-0.5 rounded bg-blue-100 hover:bg-blue-200 font-semibold text-blue-800"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-5 text-center text-gray-400">
                        No notifications yet.
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`px-4 py-3 border-b last:border-b-0 cursor-pointer ${
                            n.read ? "bg-white" : "bg-blue-50"
                          }`}
                          onClick={() => {
                            // If notification has type "kyc" or similar, redirect to profile
                            if (
                              n.status === "approved" ||
                              n.status === "declined"
                            ) {
                              navigate("/profile");
                              setNotifOpen(false);
                            }
                          }}
                        >
                          <div className="text-sm font-medium text-gray-800">
                            {n.status === "approved" ? (
                              <span className="text-green-700 font-bold">
                                ✅ Approved
                              </span>
                            ) : n.status === "declined" ? (
                              <span className="text-red-600 font-bold">
                                ❌ Declined
                              </span>
                            ) : (
                              "Notification"
                            )}
                          </div>
                          <div className="text-gray-700 text-sm mt-1">
                            {n.message}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {n.createdAt?.seconds
                              ? new Date(
                                  n.createdAt.seconds * 1000,
                                ).toLocaleString()
                              : ""}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profile Avatar Dropdown */}
          {user && (
            <div className="relative ml-2" ref={dropdownRef}>
              <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center focus:outline-none"
                aria-label="Open profile menu"
              >
                {userData && userData.profilePic ? (
                  <img
                    src={userData.profilePic}
                    alt="Profile"
                    className="h-10 w-10 rounded-full object-cover border-2 border-blue-500 shadow"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center border-2 border-gray-400">
                    <svg
                      className="h-6 w-6 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                      />
                    </svg>
                  </div>
                )}
              </button>
              {open && (
                <div className="absolute right-0 mt-2 w-48 bg-white/80 border border-gray-100 rounded-xl shadow-xl z-50 backdrop-blur-xl animate-fadeIn">
                  <button
                    className="w-full px-4 py-3 text-left hover:bg-fuchsia-50 font-semibold rounded-t-xl transition"
                    onClick={() => {
                      setOpen(false);
                      navigate("/profile");
                    }}
                  >
                    Profile
                  </button>
                  <button
                    className="w-full px-4 py-3 text-left hover:bg-fuchsia-50 text-red-600 font-semibold rounded-b-xl transition"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
//
